import { Button, Checkbox, Dropdown, Input, Spin, Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CaretDownOutlined, SearchOutlined } from '@ant-design/icons';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useToggleVisibility } from '../hooks';
import { theme } from '../styles/theme';

export type Value = number | string | boolean;

export const normalizeText = (text: string): string => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

export interface IFilter {
  label: string;
  value: Value;
}

export interface IProps<T> {
  name: string;
  filters: IFilter[];
  selected: T[];
  selectedOnReset?: T[];
  placement?: 'bottomLeft' | 'bottomRight';
  icon?: React.ReactNode;
  searchable?: boolean;
  onApply: (newSelection: T[]) => void;
  size?: SizeType;
  maxToShow?: number;
  minWidth?: number;
  renderButton?({
    numberOfActive,
  }: {
    numberOfActive: number;
  }): React.ReactElement;
  onSearch?: (query: string) => void;
  search?: string;
  isLoading?: boolean;
  visible?: boolean;
  onShow?: () => void;
  onHide?: () => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}

const Text = Typography.Text;

const ModelInstanceFilter: React.FC<IProps<any>> = ({
  isLoading,
  name,
  placement = 'bottomLeft',
  filters,
  selected,
  selectedOnReset = [],
  icon,
  searchable,
  size = 'middle',
  maxToShow,
  minWidth,
  search,
  visible,
  onApply,
  onSearch,
  renderButton,
  onShow,
  onHide,
  onScroll,
}) => {
  const searchControlledOutside = search !== undefined;

  const { isVisible, show, hide } = useToggleVisibility();
  const [localSelected, setLocalSelected] = useState(selected);
  const [searchInput, setSearchInput] = useState<string>('');

  const searchValue = searchControlledOutside ? search : searchInput;

  const visibleValue = visible !== undefined ? visible : isVisible;

  const numberToShow = maxToShow || filters.length || 100;

  const searchedFilters = useMemo(() => {
    const term = normalizeText(searchValue);
    const base = searchControlledOutside
      ? filters
      : filters.filter(({ label }) =>
          normalizeText(label || '').includes(term)
        );

    const uniqueFilters = base.filter(
      (filter, index, self) =>
        index === self.findIndex(t => t.value === filter.value)
    );

    return uniqueFilters.slice(0, numberToShow);
  }, [filters, searchValue, numberToShow, searchControlledOutside]);

  useEffect(() => {
    setLocalSelected(selected);
  }, [selected]);

  useEffect(() => {
    if (!isVisible) {
      setTimeout(() => {
        setSearchInput('');
      }, 300);
    }
  }, [isVisible]);

  const handleChangeFilter = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    const newSelection = localSelected.filter(item => item !== value);
    if (checked) {
      newSelection.push(value);
    }
    setLocalSelected(newSelection);
  };

  const handleApply = () => {
    onApply(localSelected);
    hide();
  };

  const handleClear = () => {
    setLocalSelected(selectedOnReset || []);
    onApply(selectedOnReset);
    hide();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (searchControlledOutside && onSearch) {
      show();
      onSearch(e.target.value);
      return;
    }

    setSearchInput(e.target.value);
  };

  const handleShow = () => {
    if (onShow) {
      onShow();
      return;
    }
    show();
  };

  const handleHide = () => {
    if (onHide) {
      onHide();
      return;
    }
    hide();
  };

  const renderTrigger = () => {
    if (renderButton) {
      return renderButton({ numberOfActive: selected.length });
    }

    return (
      <ButtonFilter size={size}>
        <span>{name}</span>
        {selected.length > 0 ? (
          <ActiveFilters>
            <span>{selected.length}</span>
          </ActiveFilters>
        ) : icon ? (
          icon
        ) : (
          <CaretDownOutlined />
        )}
      </ButtonFilter>
    );
  };

  return (
    <Dropdown
      popupRender={() => (
        <StyledContent
          $minWidth={minWidth}
          onMouseDown={e => e.stopPropagation()}
        >
          {searchable && (filters.length > 0 || searchValue) && (
            <Input
              value={searchValue}
              onChange={handleSearch}
              placeholder={'Search'}
              prefix={<SearchOutlined size={10} />}
              allowClear={true}
              style={{ marginBottom: theme.spacing.xs }}
              onMouseDown={(e: React.MouseEvent<HTMLInputElement>) =>
                e.stopPropagation()
              }
              size={'small'}
            />
          )}
          <ItemsWrapper onScroll={onScroll}>
            {searchedFilters.map(filter => (
              <div key={`${filter.label}_${filter.value}`}>
                <Checkbox
                  name={filter.label}
                  value={filter.value}
                  checked={localSelected.includes(filter.value)}
                  onChange={handleChangeFilter}
                >
                  {filter.label}
                </Checkbox>
              </div>
            ))}
            {isLoading && <Spin size="small" />}
            {!isLoading && filters.length === 0 && (
              <Text color={theme.colors.textSecondary}>No data</Text>
            )}
          </ItemsWrapper>
          <WrapperButtons>
            <ApplyButton size="small" type="link" onClick={handleClear}>
              Clear
            </ApplyButton>
            <ApplyButton size="small" type="primary" onClick={handleApply}>
              Apply
            </ApplyButton>
          </WrapperButtons>
        </StyledContent>
      )}
      trigger={['click']}
      open={visibleValue}
      onOpenChange={visible => {
        visible ? handleShow() : handleHide();
      }}
      placement={placement}
    >
      {renderTrigger()}
    </Dropdown>
  );
};

export const ActiveFilters = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${theme.spacing.md};
  height: ${theme.spacing.md};
  background-color: ${theme.colors.primary};
  border-radius: 100%;
  margin-left: ${theme.spacing.xs};
  font-size: 12px;
  color: ${theme.colors.white};
`;

export const StyledContent = styled.div<{ $minWidth?: number }>`
  box-shadow: 0 0 12px 0 rgba(34, 48, 92, 0.08);
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  .ant-checkbox-wrapper {
    font-size: 12px;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
  max-width: 400px;
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : undefined)};
`;

export const WrapperButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const ApplyButton = styled(Button)`
  &&& {
    margin-top: ${({ theme }) => theme.spacing.sm};
    padding: 2px ${({ theme }) => theme.spacing.sm};
    font-size: 12px;
    margin-left: ${({ theme }) => theme.spacing.xs};
    height: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ItemsWrapper = styled.div`
  max-height: 250px;
  overflow: auto;
`;

export const ButtonFilter = styled(Button)`
  display: flex;
  min-width: 105px;
  justify-content: center;
  align-items: center;
`;

export default ModelInstanceFilter;
